import { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ConfirmDialog, toast } from '@core/components';
import { useIsMobile } from '@core/hooks/useIsMobile';
import type { Order } from '../../api/order';
import {
  confirmProductionPrint,
  confirmProductionReprint,
} from '../../api/productionItems';
import {
  type JobOrder,
  type JobOrderPrintHandle,
  loadJobOrdersByItemIds,
  loadQueuedJobOrders,
  runBrowserPrint,
} from '../utils/jobOrderPrint';
import JobOrderPrint from './JobOrder';

export const useJobOrderPrint = (
  options: {
    onQueueChange?: () => void | Promise<void>;
  } = {}
) => {
  const { onQueueChange } = options;
  const isMobile = useIsMobile();
  const isPrintAvailable = !isMobile;
  const [jobOrdersToPrint, setJobOrdersToPrint] = useState<JobOrder[] | null>(
    null
  );
  const [attestationOpen, setAttestationOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const isPrintingRef = useRef(false);
  const printRef = useRef<JobOrderPrintHandle>(null);
  const pendingConfirmRef = useRef<(() => Promise<void>) | null>(null);

  const runExclusive = useCallback(async (task: () => Promise<void>) => {
    if (isPrintingRef.current) return;
    isPrintingRef.current = true;
    try {
      await task();
    } finally {
      isPrintingRef.current = false;
    }
  }, []);

  const print = useCallback(
    async ({
      input,
      onConfirm,
    }: {
      input: Order | JobOrder[];
      onConfirm: () => Promise<void>;
    }) => {
      if (!isPrintAvailable) {
        toast.error('Job order printing is only available on desktop');
        return;
      }
      setJobOrdersToPrint(
        Array.isArray(input)
          ? input
          : input.items.map((item) => ({ order: input, item }))
      );
      try {
        await runBrowserPrint(printRef);
        pendingConfirmRef.current = onConfirm;
        setAttestationOpen(true);
      } finally {
        setJobOrdersToPrint(null);
      }
    },
    [isPrintAvailable]
  );

  const printJobOrders = useCallback(
    (args: Parameters<typeof print>[0]) => runExclusive(() => print(args)),
    [print, runExclusive]
  );

  const printQueuedJobOrders = useCallback(async () => {
    if (!isPrintAvailable) {
      toast.error('Job order printing is only available on desktop');
      return;
    }
    await runExclusive(async () => {
      try {
        const queued = await loadQueuedJobOrders();
        if (!queued) {
          toast('No job orders in the print queue');
          return;
        }
        await print({
          input: queued.jobOrders,
          onConfirm: async () => {
            await confirmProductionPrint(queued.itemIds);
            await onQueueChange?.();
          },
        });
      } catch {
        toast.error('Failed to prepare job orders for printing');
      }
    });
  }, [isPrintAvailable, onQueueChange, print, runExclusive]);

  const reprintFromHistory = useCallback(
    (itemIds: number[]) =>
      runExclusive(async () => {
        try {
          const reprint = await loadJobOrdersByItemIds(itemIds);
          if (!reprint) {
            toast('None of these items are still in production');
            return;
          }
          const skipped = itemIds.length - reprint.itemIds.length;
          if (skipped > 0) {
            toast(
              `Skipping ${skipped} ${skipped === 1 ? 'item' : 'items'} no longer in production`
            );
          }
          await print({
            input: reprint.jobOrders,
            onConfirm: async () => {
              await confirmProductionReprint(reprint.itemIds);
              await onQueueChange?.();
            },
          });
        } catch {
          toast.error('Failed to prepare job orders for reprinting');
        }
      }),
    [onQueueChange, print, runExclusive]
  );

  const confirmPrint = useCallback(async () => {
    if (!pendingConfirmRef.current) return;
    setConfirmLoading(true);
    try {
      await pendingConfirmRef.current();
      pendingConfirmRef.current = null;
      setAttestationOpen(false);
    } catch {
      toast.error('Failed to confirm print');
    } finally {
      setConfirmLoading(false);
    }
  }, []);

  const declinePrint = useCallback(() => {
    pendingConfirmRef.current = null;
    setAttestationOpen(false);
    toast('Items remain in the print queue');
  }, []);

  return {
    isPrintAvailable,
    printJobOrders,
    printQueuedJobOrders,
    reprintFromHistory,
    printView:
      jobOrdersToPrint && typeof document !== 'undefined'
        ? createPortal(
            <JobOrderPrint ref={printRef} jobOrders={jobOrdersToPrint} />,
            document.body
          )
        : null,
    attestationDialog: (
      <ConfirmDialog
        open={attestationOpen}
        title="Confirm print"
        content="Did the job orders print successfully?"
        confirmText="Yes"
        cancelText="No"
        onConfirm={confirmPrint}
        onCancel={declinePrint}
        loading={confirmLoading}
      />
    ),
  };
};
